import { Link } from "react-router-dom";
import {
  FileUp, Zap, BarChart3, Shield, Download, Users,
  ArrowRight, FileText, Calculator, TrendingUp, ChevronRight, MessageSquare,
} from "lucide-react";

const REQUEST_ACCESS = "/request-access";

/*
 CTA catalogue — all labels that route to /request-access:
   "Request Access"  — nav bar button, hero primary CTA, footer link
   "Get Started"     — final CTA section
*/

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-[#FAFAF5]">
      {/* ── Nav ── */}
      <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-md border-b border-grove-300">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-8 h-16 flex items-center justify-between">
          <a
            href="#"
            onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); }}
            className="flex items-center gap-3"
          >
            <div className="w-9 h-9 rounded-lg bg-forest-700 flex items-center justify-center shadow-md">
              <span className="text-white font-bold text-sm tracking-tight">AV</span>
            </div>
            <span className="font-display text-xl font-semibold tracking-tight text-bark-800">
              AutoVal
            </span>
          </a>
          <div className="flex items-center gap-4">
            <Link to="/sign-in" className="text-sm font-medium text-bark-500 hover:text-bark-800 transition-colors">
              Sign In
            </Link>
            <Link
              to={REQUEST_ACCESS}
              className="px-5 py-2.5 bg-forest-700 text-white text-sm font-semibold rounded-lg hover:bg-forest-600 transition-all shadow-sm"
            >
              Request Access
            </Link>
          </div>
        </div>
      </header>

      {/* ── Hero ── */}
      <section className="max-w-[1280px] mx-auto px-4 sm:px-8 pt-20 pb-16 text-center animate-fade-in">
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold text-bark-800 tracking-tight leading-tight max-w-3xl mx-auto mb-6">
          Automated CRE Cash&nbsp;Flow Underwriting
        </h1>
        <p className="text-lg sm:text-xl text-bark-400 max-w-2xl mx-auto mb-10 leading-relaxed">
          Upload your rent rolls and operating statements. AutoVal builds a complete,
          formula-driven cash flow model in minutes — not the days it takes in a spreadsheet.
          Adjust any assumption in plain English and export a working Excel file.
        </p>
        <div className="flex flex-wrap items-center justify-center gap-4">
          <Link
            to={REQUEST_ACCESS}
            className="inline-flex items-center gap-2 px-8 py-3.5 bg-forest-700 text-white text-sm font-semibold rounded-lg hover:bg-forest-600 hover:shadow-lg transition-all shadow-md"
          >
            Request Access
            <ArrowRight className="w-4 h-4" />
          </Link>
          <a
            href="#how-it-works"
            className="inline-flex items-center gap-2 px-6 py-3.5 text-sm font-medium text-bark-500 hover:text-bark-800 border border-grove-300 rounded-lg hover:bg-white transition-all"
          >
            See How It Works
          </a>
        </div>
      </section>

      {/* ── Dashboard preview ── */}
      <section className="max-w-[1080px] mx-auto px-4 sm:px-8 pb-20">
        <div className="bg-white rounded-2xl border border-grove-300 shadow-xl overflow-hidden">
          <div className="bg-grove-100 border-b border-grove-300 px-4 py-3 flex items-center gap-2">
            <div className="flex gap-1.5">
              <div className="w-3 h-3 rounded-full bg-bark-200" />
              <div className="w-3 h-3 rounded-full bg-bark-200" />
              <div className="w-3 h-3 rounded-full bg-bark-200" />
            </div>
            <div className="flex-1 mx-4">
              <div className="bg-white rounded-md px-3 py-1 text-xs text-bark-300 text-center border border-grove-200">
                autoval.app/deals/riverchase-plaza
              </div>
            </div>
          </div>
          <div className="p-6 sm:p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-8 rounded-lg bg-forest-700 flex items-center justify-center">
                <span className="text-white font-bold text-xs">AV</span>
              </div>
              <div>
                <p className="text-sm font-semibold text-bark-800">Riverchase Shopping Center</p>
                <p className="text-xs text-bark-400">100 Riverchase Pkwy, Birmingham, AL</p>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
              {[
                { label: "NOI", value: "$2.84M", sub: "Year 1" },
                { label: "Occupancy", value: "94.2%", sub: "Current" },
                { label: "IRR", value: "12.6%", sub: "Levered" },
                { label: "DSCR", value: "1.42x", sub: "Year 1" },
              ].map((m) => (
                <div key={m.label} className="bg-grove-50 rounded-lg border border-grove-200 p-4">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-bark-400 mb-1">{m.label}</p>
                  <p className="text-xl font-semibold text-bark-800 tabular-nums">{m.value}</p>
                  <p className="text-[10px] text-bark-300 mt-0.5">{m.sub}</p>
                </div>
              ))}
            </div>
            <div className="rounded-lg border border-grove-200 overflow-hidden">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-grove-100">
                    <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-bark-400">Line Item</th>
                    {["Yr 1", "Yr 2", "Yr 3", "Yr 4", "Yr 5"].map((y) => (
                      <th key={y} className="text-right px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-bark-400 hidden sm:table-cell">{y}</th>
                    ))}
                    <th className="text-right px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-bark-400 sm:hidden">Yr 1</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { item: "Base Rent", vals: ["2,145,600", "2,209,968", "2,276,267", "2,344,555", "2,414,892"] },
                    { item: "Reimbursements", vals: ["694,200", "714,426", "735,279", "756,737", "778,839"] },
                    { item: "EGI", vals: ["2,839,800", "2,924,394", "3,011,546", "3,101,292", "3,193,731"], bold: true },
                    { item: "Expenses", vals: ["(956,400)", "(984,492)", "(1,013,427)", "(1,043,230)", "(1,073,927)"] },
                    { item: "NOI", vals: ["1,883,400", "1,939,902", "1,998,119", "2,058,062", "2,119,804"], bold: true },
                  ].map((row, i) => (
                    <tr key={row.item} className={i % 2 === 1 ? "bg-grove-50" : ""}>
                      <td className={`px-3 py-2 ${row.bold ? "font-semibold text-bark-800" : "text-bark-600"}`}>{row.item}</td>
                      {row.vals.map((v, j) => (
                        <td key={j} className={`text-right px-3 py-2 tabular-nums ${j === 0 ? "" : "hidden sm:table-cell"} ${row.bold ? "font-semibold text-bark-800" : "text-bark-500"}`}>{v}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* ── How It Works ── */}
      <section id="how-it-works" className="bg-white border-y border-grove-300 py-20">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-8">
          <div className="text-center mb-14">
            <h2 className="font-display text-3xl sm:text-4xl font-semibold text-bark-800 tracking-tight mb-4">
              How It Works
            </h2>
            <p className="text-bark-400 text-lg max-w-xl mx-auto">
              From raw documents to a full underwriting model in minutes, not days.
            </p>
          </div>
          <div className="grid sm:grid-cols-3 gap-8">
            {[
              {
                icon: FileUp,
                step: "01",
                title: "Upload Documents",
                desc: "Drop in your rent rolls, operating statements, and CAM reconciliations. PDF or Excel — AutoVal handles both.",
              },
              {
                icon: Zap,
                step: "02",
                title: "Automatic Model Build",
                desc: "AutoVal uses its proprietary AI technology to transform your documents into a complete cash flow model with live Excel formulas — not static numbers. Every assumption is editable, every figure is traceable back to its source document.",
              },
              {
                icon: BarChart3,
                step: "03",
                title: "Analyze & Share",
                desc: "Run scenarios, compare assumptions, and export detailed Excel workbooks. Invite buyers into a credentialed deal room.",
              },
            ].map((s) => (
              <div key={s.step}>
                <div className="w-12 h-12 rounded-xl bg-forest-50 border border-forest-200 flex items-center justify-center mb-5">
                  <s.icon className="w-5 h-5 text-forest-700" />
                </div>
                <span className="text-xs font-bold text-forest-400 uppercase tracking-widest mb-2 block">Step {s.step}</span>
                <h3 className="font-display text-lg font-semibold text-bark-800 mb-2">{s.title}</h3>
                <p className="text-sm text-bark-400 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── AutoVal Chat ── */}
      <section className="py-20 bg-[#FAFAF5]">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-honey-50 border border-honey-200 rounded-full text-honey-700 text-xs font-medium mb-6">
                <MessageSquare className="w-3.5 h-3.5" />
                New Feature
              </div>
              <h2 className="font-display text-3xl sm:text-4xl font-semibold text-bark-800 tracking-tight mb-4">
                Adjust Your Model in&nbsp;Plain&nbsp;English
              </h2>
              <p className="text-bark-400 text-lg leading-relaxed mb-8">
                AutoVal Chat understands CRE. Type what you want in conversational language and watch the cash flow table update in real time. No spreadsheet gymnastics required.
              </p>
              <div className="space-y-3">
                {[
                  { q: '"Set vacancy to 7%"', a: "Updates general vacancy and reruns the full projection instantly." },
                  { q: '"Widen the exit cap by 25 bps"', a: "Computes the new exit cap rate and shows the impact on IRR and equity multiple." },
                  { q: '"Run a conservative scenario"', a: "Suggests higher vacancy, lower rent growth, wider exit cap — you confirm before applying." },
                  { q: '"Stress test exit cap rate"', a: "Creates three scenarios (downside, base, upside) so you can compare returns side by side." },
                  { q: '"Add a dollar to market rent"', a: "Knows the current market rent, adds $1/SF, and summarizes what changed." },
                ].map((ex) => (
                  <div key={ex.q} className="bg-white rounded-xl border border-grove-300 px-5 py-3.5">
                    <p className="text-sm font-semibold text-forest-700 mb-0.5">{ex.q}</p>
                    <p className="text-xs text-bark-400">{ex.a}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white rounded-2xl border border-grove-300 shadow-xl p-6 max-w-md mx-auto lg:mx-0">
              <div className="flex items-center gap-2 mb-4 pb-3 border-b border-grove-200">
                <MessageSquare className="w-4 h-4 text-forest-600" />
                <span className="text-sm font-semibold text-bark-700">AutoVal Chat</span>
              </div>
              <div className="space-y-3 mb-4">
                <div className="flex justify-end">
                  <div className="bg-forest-600 text-white rounded-xl px-3 py-2 text-sm max-w-[80%]">
                    Assume 7 percent vacancy and widen exit cap by 50 bps
                  </div>
                </div>
                <div className="flex justify-start">
                  <div className="bg-grove-50 border border-grove-200 rounded-xl px-3 py-2 text-sm text-bark-700 max-w-[85%]">
                    <p>Setting vacancy to 7% and exit cap to 7.00% (was 6.50%).</p>
                    <p className="text-xs text-bark-400 mt-1.5 border-t border-grove-200 pt-1.5">
                      Year 1 NOI dropped $42K to $1.84M. Unlevered IRR fell 85bps to 11.7%. Exit value decreased $1.1M due to the wider cap rate.
                    </p>
                  </div>
                </div>
                <div className="flex justify-end">
                  <div className="bg-forest-600 text-white rounded-xl px-3 py-2 text-sm max-w-[80%]">
                    Save as scenario Conservative Case
                  </div>
                </div>
                <div className="flex justify-start">
                  <div className="bg-grove-50 border border-grove-200 rounded-xl px-3 py-2 text-sm text-bark-700">
                    Saved scenario &ldquo;Conservative Case&rdquo;.
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex-1 px-3 py-2 text-sm border border-grove-300 rounded-lg text-bark-300">
                  e.g. &ldquo;increase market rent by $1&rdquo;
                </div>
                <div className="p-2 rounded-lg bg-forest-600">
                  <ArrowRight className="w-4 h-4 text-white" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Features ── */}
      <section className="py-20">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-8">
          <div className="text-center mb-14">
            <h2 className="font-display text-3xl sm:text-4xl font-semibold text-bark-800 tracking-tight mb-4">
              Built for CRE Professionals
            </h2>
            <p className="text-bark-400 text-lg max-w-xl mx-auto">
              Everything you need to underwrite commercial properties with confidence.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: MessageSquare, title: "AutoVal Chat", desc: "Modify your cash flow model using plain English. Say \"set vacancy to 7%\" or \"stress test exit cap\" — the model updates in real time with a plain-English impact summary." },
              { icon: FileText, title: "Multi-Format Parsing", desc: "Rent rolls, operating statements, and CAM recs — PDF or Excel. Deterministic extraction with AI enhancement when needed." },
              { icon: Calculator, title: "Full Cash Flow Engine", desc: "10-year projections with levered returns, DSCR, and six reimbursement structures — NNN, gross, modified gross, base year stop, expense cap, and FSG." },
              { icon: TrendingUp, title: "Scenario & Sensitivity", desc: "Bear, base, and bull cases with sensitivity grids. Adjust rent growth, vacancy, cap rates, and debt assumptions — compare results side by side." },
              { icon: Shield, title: "6-Tab Audit System", desc: "Verify every number. Rent schedules, reimbursement waterfalls, expense growth, occupancy, rollover, and return metrics." },
              { icon: Download, title: "Formula-Driven Excel Export", desc: "Download a working Excel model with 450+ live formulas. Change an assumption and watch rent, expenses, reimbursements, and returns recalculate — just like ARGUS, but built automatically." },
            ].map((f) => (
              <div key={f.title} className="bg-white rounded-xl border border-grove-300 p-6 hover:shadow-md hover:border-grove-400 transition-all">
                <div className="w-10 h-10 rounded-lg bg-honey-50 border border-honey-200 flex items-center justify-center mb-4">
                  <f.icon className="w-5 h-5 text-honey-700" />
                </div>
                <h3 className="font-display text-base font-semibold text-bark-800 mb-2">{f.title}</h3>
                <p className="text-sm text-bark-400 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Stats banner ── */}
      <section className="bg-forest-900 py-20">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-8">
          <div className="grid sm:grid-cols-3 gap-8 text-center">
            {[
              { value: "15+", label: "Document formats supported", sub: "Brixmor, Yardi, custom Excel, generic PDF" },
              { value: "0–100", label: "Confidence scoring on every parse", sub: "AI automatically enhances low-confidence fields" },
              { value: "6", label: "Reimbursement structures", sub: "NNN, gross, modified gross, base year stop, expense cap, FSG" },
            ].map((s) => (
              <div key={s.label}>
                <p className="text-4xl sm:text-5xl font-bold text-white mb-2 tabular-nums">{s.value}</p>
                <p className="text-sm font-medium text-forest-300 mb-1">{s.label}</p>
                <p className="text-xs text-forest-400">{s.sub}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Final CTA ── */}
      <section className="py-20">
        <div className="max-w-2xl mx-auto px-4 sm:px-8 text-center">
          <h2 className="font-display text-3xl sm:text-4xl font-semibold text-bark-800 tracking-tight mb-4">
            Ready to automate your underwriting?
          </h2>
          <p className="text-bark-400 text-lg mb-8 leading-relaxed">
            Stop rebuilding cash flow models from scratch. Upload your documents and let AutoVal do the heavy lifting.
          </p>
          <Link
            to={REQUEST_ACCESS}
            className="inline-flex items-center gap-2 px-8 py-3.5 bg-honey-600 text-white text-sm font-semibold rounded-lg hover:bg-honey-500 hover:shadow-lg transition-all shadow-md"
          >
            Get Started
            <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="border-t border-grove-300 py-8">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-md bg-forest-700 flex items-center justify-center">
                <span className="text-white font-bold text-[10px] tracking-tight">AV</span>
              </div>
              <span className="text-sm text-bark-400">AutoVal</span>
            </div>
            <div className="flex items-center gap-6">
              <Link to="/sign-in" className="text-sm text-bark-400 hover:text-bark-700 transition-colors">Sign In</Link>
              <Link to={REQUEST_ACCESS} className="text-sm text-bark-400 hover:text-bark-700 transition-colors">Request Access</Link>
            </div>
          </div>
          <p className="text-xs text-bark-300">&copy; {new Date().getFullYear()} AutoVal. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
